class ProductReviewsJudgeme extends HTMLElement {
  constructor() {
    super();
    this.onReady = this.onReady.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onShowMoreClick = this.onShowMoreClick.bind(this);
    this.scheduleNormalize = this.scheduleNormalize.bind(this);
    this.onStarFilterClick = this.onStarFilterClick.bind(this);
    this.activeStarFilter = null;
    this.desktopExpanded = false;
    this.isNormalizing = false;
    this.normalizeQueued = false;
    this.widgetNormalized = false;
    this.observer = null;
    this.retryTimeoutIds = [];
  }

  connectedCallback() {
    this.toggleButton = this.querySelector('[data-show-more-reviews]');
    this.toggleButton?.addEventListener('click', this.onShowMoreClick);
    this.addEventListener('click', this.onStarFilterClick);
    window.addEventListener('resize', this.onResize);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.onReady);
    } else {
      this.onReady();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('DOMContentLoaded', this.onReady);
    window.removeEventListener('resize', this.onResize);
    this.toggleButton?.removeEventListener('click', this.onShowMoreClick);
    this.removeEventListener('click', this.onStarFilterClick);
    this.disconnectObserver();
    this.clearRetryTimeouts();
  }

  get widgetContainer() {
    return this.querySelector('[data-judgeme-widget]');
  }

  get isDesktop() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  clearRetryTimeouts() {
    this.retryTimeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    this.retryTimeoutIds = [];
  }

  disconnectObserver() {
    this.observer?.disconnect();
    this.observer = null;
  }

  scheduleNormalize() {
    if (this.widgetNormalized || this.normalizeQueued) return;

    this.normalizeQueued = true;
    requestAnimationFrame(() => {
      this.normalizeQueued = false;
      this.normalizeWidget();
    });
  }

  hasWidgetContent() {
    const container = this.widgetContainer;
    if (!container) return false;

    return Boolean(
      container.querySelector('.jdgm-rev-widg, .jdgm-rev, .jdgm-rev-widg__summary')
    );
  }

  waitForWidget(timeoutMs) {
    return new Promise((resolve) => {
      const startedAt = Date.now();

      const check = () => {
        if (this.hasWidgetContent() || Date.now() - startedAt >= timeoutMs) {
          resolve();
          return;
        }

        requestAnimationFrame(check);
      };

      check();
    });
  }

  async fetchProductReviewWidget() {
    const shopDomain = this.dataset.shopDomain;
    const apiToken = this.dataset.apiToken;
    const productId = this.dataset.productId;

    if (!shopDomain || !apiToken || !productId) return '';

    try {
      const url = new URL('https://judge.me/api/v1/widgets/product_review');
      url.searchParams.set('shop_domain', shopDomain);
      url.searchParams.set('api_token', apiToken);
      url.searchParams.set('external_id', productId);

      const response = await fetch(url.toString());
      if (!response.ok) return '';

      const data = await response.json();
      return data.widget || data.html || '';
    } catch (error) {
      return '';
    }
  }

  triggerJudgemeInit() {
    if (this.judgemeInitialized) return;

    this.judgemeInitialized = true;

    if (window.jdgm?.CUSTOMIZE) {
      window.jdgm.CUSTOMIZE();
    }

    if (window.jdgm?.customizeBadges) {
      window.jdgm.customizeBadges();
    }
  }

  static STAR_PATH =
    'M3.816 20L5.444 12.6L0 7.62L7.18 6.964L10 0L12.82 6.964L20 7.62L14.556 12.6L16.184 20L10 16.08L3.816 20Z';

  createHistogramStars(rowRating) {
    const starsTemplate = this.querySelector('[data-histogram-stars-template]');
    const templateStars = starsTemplate?.content?.querySelector(
      `[data-histogram-stars="${rowRating}"]`
    );

    if (templateStars) {
      return templateStars.cloneNode(true);
    }

    const container = document.createElement('span');
    container.className = 'product-reviews-pilgrim__histogram-stars';
    container.dataset.histogramStars = String(rowRating);

    for (let index = 1; index <= 5; index += 1) {
      const state = index <= rowRating ? 'full' : 'empty';
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', `product-pilgrim__rating-star-icon product-pilgrim__rating-star-icon--${state}`);
      svg.setAttribute('width', '20');
      svg.setAttribute('height', '20');
      svg.setAttribute('viewBox', '0 0 20 20');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('class', `product-pilgrim__rating-star-shape product-pilgrim__rating-star-shape--${state}`);
      path.setAttribute('d', ProductReviewsJudgeme.STAR_PATH);
      svg.appendChild(path);
      container.appendChild(svg);
    }

    return container;
  }

  getHistogramRows(histogram) {
    const rows = histogram.querySelectorAll(
      '.jdgm-histogram__row:not(.jdgm-histogram__clear-filter)'
    );

    if (rows.length) {
      return Array.from(rows).filter((row) => this.rowHasHistogramBar(row));
    }

    return Array.from(histogram.children).filter((row) => this.rowHasHistogramBar(row));
  }

  rowHasHistogramBar(row) {
    return Boolean(
      row.querySelector('.jdgm-histogram__bar') ||
        row.querySelector('[class*="histogram"][class*="bar"]')
    );
  }

  getHistogramRowRating(row, index) {
    const rating = Number.parseInt(row.dataset.rating, 10);
    if (Number.isFinite(rating) && rating >= 1 && rating <= 5) {
      return rating;
    }

    return Math.max(1, 5 - index);
  }

  getHistogramStarCell(row) {
    const existingStarCell =
      row.querySelector('.jdgm-histogram__star') ||
      row.querySelector('[class*="histogram"][class*="star"]');

    if (existingStarCell && !existingStarCell.classList.contains('jdgm-histogram__bar')) {
      return existingStarCell;
    }

    const bar =
      row.querySelector('.jdgm-histogram__bar') ||
      row.querySelector('[class*="histogram"][class*="bar"]');
    if (!bar) return null;

    const beforeBar = bar.previousElementSibling;
    if (
      beforeBar &&
      !beforeBar.classList.contains('jdgm-histogram__bar') &&
      !beforeBar.classList.contains('jdgm-histogram__frequency') &&
      !beforeBar.classList.contains('jdgm-histogram__percentage')
    ) {
      return beforeBar;
    }

    const starCell = document.createElement('div');
    starCell.className = 'jdgm-histogram__star product-reviews-pilgrim__histogram-star-cell';
    row.insertBefore(starCell, bar);
    return starCell;
  }

  mountHistogramStars(starCell, rowRating) {
    if (!starCell || starCell.classList.contains('jdgm-histogram__bar')) return false;

    if (starCell.querySelector('.product-reviews-pilgrim__histogram-stars')) {
      starCell.classList.add('product-reviews-pilgrim__histogram-star-cell');
      return false;
    }

    const stars = this.createHistogramStars(rowRating);
    if (!stars) return false;

    starCell.replaceChildren(stars);
    starCell.classList.add('product-reviews-pilgrim__histogram-star-cell');
    return true;
  }

  normalizeHistogram(container) {
    const histogram =
      container.querySelector('.jdgm-histogram') ||
      container.querySelector('[class*="jdgm"][class*="histogram"]');
    if (!histogram) return false;

    let changed = false;

    histogram.querySelectorAll('.jdgm-histogram__percentage').forEach((node) => {
      if (!node.hidden) {
        node.hidden = true;
        node.setAttribute('aria-hidden', 'true');
        changed = true;
      }
    });

    let mountedRows = 0;

    this.getHistogramRows(histogram).forEach((row, index) => {
      const rowRating = this.getHistogramRowRating(row, index);
      const starCell = this.getHistogramStarCell(row);

      if (this.mountHistogramStars(starCell, rowRating)) {
        changed = true;
      }

      if (starCell?.querySelector('.product-reviews-pilgrim__histogram-stars')) {
        mountedRows += 1;
      }

      const frequency = row.querySelector('.jdgm-histogram__frequency');
      if (frequency) {
        const cleaned = frequency.textContent.replace(/[()%]/g, '').trim();
        if (frequency.textContent !== cleaned) {
          frequency.textContent = cleaned;
          changed = true;
        }
      }
    });

    if (mountedRows > 0) {
      container.classList.add('product-reviews-pilgrim__widget--has-pilgrim-histogram');
    }

    return changed || mountedRows > 0;
  }

  createPilgrimSummary(container) {
    const template = this.querySelector('[data-reviews-summary-template]');
    if (template?.content?.firstElementChild) {
      return template.content.firstElementChild.cloneNode(true);
    }

    const rating = this.dataset.rating;
    const reviewCount = this.dataset.reviewCount;
    if (!rating && !reviewCount) {
      return this.createPilgrimSummaryFromPage(container);
    }

    const summary = document.createElement('div');
    summary.className = 'product-reviews-pilgrim__summary product-reviews-pilgrim__summary--ready';
    summary.dataset.pilgrimSummary = '';

    const average = document.createElement('span');
    average.className = 'product-reviews-pilgrim__summary-average';
    average.setAttribute('aria-hidden', 'true');
    average.textContent = rating || '0.0';
    summary.appendChild(average);

    const details = document.createElement('div');
    details.className = 'product-reviews-pilgrim__summary-details';

    const pageStars = document.querySelector('.product-pilgrim__rating-stars');
    if (pageStars) {
      const stars = pageStars.cloneNode(true);
      stars.className = 'product-reviews-pilgrim__summary-stars';
      stars.removeAttribute('role');
      stars.removeAttribute('aria-label');
      details.appendChild(stars);
    }

    if (reviewCount) {
      const count = document.createElement('span');
      count.className = 'product-reviews-pilgrim__summary-count';
      count.textContent = `${reviewCount} Reseñas`;
      details.appendChild(count);
    }

    summary.appendChild(details);
    return summary;
  }

  createPilgrimSummaryFromPage(container) {
    const pageRating = document.querySelector('.product-pilgrim__rating');
    if (!pageRating) return null;

    const ratingValue = pageRating.querySelector('.product-pilgrim__rating-value')?.textContent?.trim();
    const pageStars = pageRating.querySelector('.product-pilgrim__rating-stars');
    const reviewCount = container?.querySelector('.jdgm-rev-widg__summary-text')?.textContent?.match(/(\d+)/)?.[1];

    if (!ratingValue && !pageStars && !reviewCount) return null;

    const summary = document.createElement('div');
    summary.className = 'product-reviews-pilgrim__summary product-reviews-pilgrim__summary--ready';
    summary.dataset.pilgrimSummary = '';

    if (ratingValue) {
      const average = document.createElement('span');
      average.className = 'product-reviews-pilgrim__summary-average';
      average.setAttribute('aria-hidden', 'true');
      average.textContent = ratingValue;
      summary.appendChild(average);
    }

    const details = document.createElement('div');
    details.className = 'product-reviews-pilgrim__summary-details';

    if (pageStars) {
      const stars = pageStars.cloneNode(true);
      stars.className = 'product-reviews-pilgrim__summary-stars';
      stars.removeAttribute('role');
      stars.removeAttribute('aria-label');
      details.appendChild(stars);
    }

    if (reviewCount) {
      const count = document.createElement('span');
      count.className = 'product-reviews-pilgrim__summary-count';
      count.textContent = `${reviewCount} Reseñas`;
      details.appendChild(count);
    }

    summary.appendChild(details);
    return summary;
  }

  normalizeSummary(container) {
    const header = container.querySelector('.jdgm-rev-widg__header');
    if (!header) return false;

    const mountedSummary = header.querySelector('[data-pilgrim-summary]');
    if (mountedSummary) {
      container.classList.add('product-reviews-pilgrim__widget--has-pilgrim-summary');
      return true;
    }

    const pilgrimSummary = this.createPilgrimSummary(container);
    if (!pilgrimSummary) return false;

    const jdgmSummary = header.querySelector('.jdgm-rev-widg__summary');
    if (jdgmSummary) {
      jdgmSummary.hidden = true;
      jdgmSummary.setAttribute('aria-hidden', 'true');
    }

    if (jdgmSummary) {
      header.insertBefore(pilgrimSummary, jdgmSummary);
    } else {
      header.prepend(pilgrimSummary);
    }

    container.classList.add('product-reviews-pilgrim__widget--has-pilgrim-summary');
    return true;
  }

  isNormalizationComplete(container) {
    const header = container.querySelector('.jdgm-rev-widg__header');
    const hasSummary = Boolean(header?.querySelector('[data-pilgrim-summary]'));
    const histogram =
      container.querySelector('.jdgm-histogram') ||
      container.querySelector('[class*="jdgm"][class*="histogram"]');

    if (!histogram) {
      return hasSummary;
    }

    const rows = this.getHistogramRows(histogram);
    if (!rows.length) {
      return hasSummary;
    }

    const rowsWithStars = rows.filter((row) =>
      row.querySelector('.product-reviews-pilgrim__histogram-stars')
    );

    const hasReviewsList = Boolean(container.querySelector('.jdgm-rev-widg__reviews'));
    const hasStarFilter = Boolean(container.querySelector('[data-pilgrim-star-filter]'));

    if (hasReviewsList && !hasStarFilter) {
      return false;
    }

    const reviews = this.getReviews(container);
    if (reviews.length) {
      const normalizedReviews = reviews.filter((review) => review.dataset.pilgrimRevReady === 'true');
      if (normalizedReviews.length !== reviews.length) {
        return false;
      }
    }

    return hasSummary && rowsWithStars.length === rows.length;
  }

  createReviewCardStars(rating) {
    const rowRating = Math.min(5, Math.max(0, Number.parseInt(rating, 10) || 0));
    const starsTemplate = this.querySelector('[data-histogram-stars-template]');
    const templateStars = starsTemplate?.content?.querySelector(
      `[data-histogram-stars="${rowRating}"]`
    );

    if (templateStars) {
      const stars = templateStars.cloneNode(true);
      stars.classList.remove('product-reviews-pilgrim__histogram-stars');
      stars.classList.add('product-reviews-pilgrim__rev-stars');
      return stars;
    }

    const container = document.createElement('span');
    container.className = 'product-reviews-pilgrim__rev-stars';

    for (let index = 1; index <= 5; index += 1) {
      const state = index <= rowRating ? 'full' : 'empty';
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', `product-pilgrim__rating-star-icon product-pilgrim__rating-star-icon--${state}`);
      svg.setAttribute('width', '20');
      svg.setAttribute('height', '20');
      svg.setAttribute('viewBox', '0 0 20 20');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('class', `product-pilgrim__rating-star-shape product-pilgrim__rating-star-shape--${state}`);
      path.setAttribute('d', ProductReviewsJudgeme.STAR_PATH);
      svg.appendChild(path);
      container.appendChild(svg);
    }

    return container;
  }

  formatReviewTimestamp(node) {
    if (!node || node.dataset.pilgrimTimestampReady === 'true') return;

    const dataContent = node.getAttribute('data-content');
    const parsedDate = dataContent ? new Date(dataContent) : new Date(node.textContent.trim());
    const months = [
      'enero',
      'febrero',
      'marzo',
      'abril',
      'mayo',
      'junio',
      'julio',
      'agosto',
      'septiembre',
      'octubre',
      'noviembre',
      'diciembre',
    ];

    if (!Number.isNaN(parsedDate.getTime())) {
      node.textContent = `${months[parsedDate.getMonth()]} ${parsedDate.getFullYear()}`;
    } else {
      node.textContent = node.textContent.trim();
    }

    node.dataset.pilgrimTimestampReady = 'true';
    node.dataset.pilgrimNormalized = 'true';
  }

  normalizeReviewCard(review) {
    if (review.dataset.pilgrimRevReady === 'true') return;

    const author = review.querySelector('.jdgm-rev__author');
    const location = review.querySelector('.jdgm-rev__location');
    const timestamp = review.querySelector('.jdgm-rev__timestamp');
    const title = review.querySelector('.jdgm-rev__title');
    const body = review.querySelector('.jdgm-rev__body');
    const rating = this.getReviewRating(review);

    review.querySelector('.jdgm-rev__icon')?.remove();

    const header = document.createElement('div');
    header.className = 'jdgm-rev__header product-reviews-pilgrim__rev-header';

    const ratingWrap = document.createElement('div');
    ratingWrap.className = 'jdgm-rev__rating product-reviews-pilgrim__rev-rating';
    if (rating) {
      ratingWrap.dataset.score = String(rating);
      review.dataset.score = String(rating);
    }
    ratingWrap.appendChild(this.createReviewCardStars(rating));
    header.appendChild(ratingWrap);

    if (timestamp) {
      this.formatReviewTimestamp(timestamp);
      header.appendChild(timestamp);
    }

    const content = document.createElement('div');
    content.className = 'jdgm-rev__content product-reviews-pilgrim__rev-content';

    const textBlock = document.createElement('div');
    textBlock.className = 'product-reviews-pilgrim__rev-text';

    if (title) {
      textBlock.appendChild(title);
    }

    if (body) {
      textBlock.appendChild(body);
    }

    content.appendChild(textBlock);

    const profile = document.createElement('div');
    profile.className = 'jdgm-rev__profile product-reviews-pilgrim__rev-profile';

    if (author) {
      profile.appendChild(author);
    }

    if (location) {
      profile.appendChild(location);
    }

    review.replaceChildren(header, content, profile);
    review.dataset.pilgrimRevReady = 'true';
  }

  normalizeReviews(container) {
    this.getReviews(container).forEach((review) => {
      if (!review.dataset.pilgrimRevReady) {
        this.normalizeReviewCard(review);
      }
    });
  }

  normalizeTimestamps(container) {
    let changed = false;

    container.querySelectorAll('.jdgm-rev__timestamp:not([data-pilgrim-timestamp-ready])').forEach((node) => {
      this.formatReviewTimestamp(node);
      changed = true;
    });

    return changed;
  }

  normalizeLoadMore(container) {
    const loadMore = container.querySelector('.jdgm-paginate__load-more');
    if (!loadMore) return false;

    if (loadMore.dataset.pilgrimNormalized !== 'true') {
      loadMore.textContent = 'cargar más reseñas';
      loadMore.dataset.pilgrimNormalized = 'true';
      loadMore.addEventListener('click', () => {
        this.widgetNormalized = false;
        this.scheduleNormalize();
        this.scheduleRetries();
      });
      return true;
    }

    return false;
  }

  getStarFilterTrigger(container, rating) {
    const ratingValue = String(rating);
    const ratingNumber = Number.parseInt(ratingValue, 10);

    const rowByDataRating = container.querySelector(`.jdgm-histogram__row[data-rating="${ratingValue}"]`);
    if (rowByDataRating) {
      return (
        rowByDataRating.querySelector('.jdgm-histogram__bar') ||
        rowByDataRating
      );
    }

    const histogram =
      container.querySelector('.jdgm-histogram') ||
      container.querySelector('[class*="jdgm"][class*="histogram"]');
    if (histogram) {
      const matchingRow = this.getHistogramRows(histogram).find(
        (row, index) => this.getHistogramRowRating(row, index) === ratingNumber
      );
      if (matchingRow) {
        return matchingRow.querySelector('.jdgm-histogram__bar') || matchingRow;
      }
    }

    return (
      container.querySelector(`.jdgm-star-filter [data-rating="${ratingValue}"]`) ||
      container.querySelector(`.jdgm-subtab__item[data-rating="${ratingValue}"]`) ||
      container.querySelector(`.jdgm-rev-widg__sort-wrapper [data-rating="${ratingValue}"]`)
    );
  }

  getReviews(container) {
    return Array.from(container.querySelectorAll('.jdgm-rev-widg__reviews > .jdgm-rev'));
  }

  getReviewRating(review) {
    const ratingEl = review.querySelector('.jdgm-rev__rating');
    const dataScore =
      ratingEl?.dataset?.score ||
      ratingEl?.getAttribute('data-score') ||
      review.dataset?.score ||
      review.getAttribute('data-score');

    if (dataScore != null && dataScore !== '') {
      const parsedScore = Math.round(Number.parseFloat(dataScore));
      if (Number.isFinite(parsedScore) && parsedScore >= 1 && parsedScore <= 5) {
        return parsedScore;
      }
    }

    const ariaLabel = ratingEl?.getAttribute('aria-label') || '';
    const ariaMatch = ariaLabel.match(/(\d)\s*star/i);
    if (ariaMatch) {
      return Number.parseInt(ariaMatch[1], 10);
    }

    const onStars = review.querySelectorAll(
      '.jdgm-rev__rating .jdgm-star.jdgm--on, .jdgm-rev__header .jdgm-star.jdgm--on'
    ).length;

    return onStars || 0;
  }

  getReviewCountForRating(container, rating) {
    const ratingValue = Number.parseInt(rating, 10);
    let histogramRow = container.querySelector(`.jdgm-histogram__row[data-rating="${ratingValue}"]`);

    if (!histogramRow) {
      const histogram =
        container.querySelector('.jdgm-histogram') ||
        container.querySelector('[class*="jdgm"][class*="histogram"]');
      if (histogram) {
        histogramRow = this.getHistogramRows(histogram).find(
          (row, index) => this.getHistogramRowRating(row, index) === ratingValue
        );
      }
    }

    const histogramCount = Number.parseInt(
      histogramRow?.querySelector('.jdgm-histogram__frequency')?.textContent?.replace(/[()%]/g, '').trim(),
      10
    );

    if (Number.isFinite(histogramCount)) {
      return histogramCount;
    }

    return this.getReviews(container).filter((review) => this.getReviewRating(review) === ratingValue).length;
  }

  getFilterEmptyMessage(container) {
    return (
      container.querySelector('[data-pilgrim-filter-empty]') ||
      this.querySelector('[data-pilgrim-filter-empty]')
    );
  }

  getFilterEmptyText(rating) {
    const stars = String(rating);
    const label = stars === '1' ? 'estrella' : 'estrellas';
    return `No hay reseñas de ${stars} ${label}`;
  }

  toggleEmptyFilterMessage(container, show) {
    const emptyMessage = this.getFilterEmptyMessage(container);
    if (!emptyMessage) return;

    const textEl = emptyMessage.querySelector('[data-pilgrim-filter-empty-text]');
    if (textEl && this.activeStarFilter) {
      textEl.textContent = this.getFilterEmptyText(this.activeStarFilter);
    }

    emptyMessage.hidden = !show;
    container?.classList.toggle('product-reviews-pilgrim__widget--has-filter-empty', show);
  }

  applyStarFilter(container, rating) {
    const ratingValue = Number.parseInt(rating, 10);
    const reviews = this.getReviews(container);
    let visibleCount = 0;

    reviews.forEach((review) => {
      const matches = this.getReviewRating(review) === ratingValue;
      review.classList.toggle('product-reviews-pilgrim__rev--filtered-out', !matches);
      if (matches) visibleCount += 1;
    });

    this.activeStarFilter = String(ratingValue);

    const expectedCount = this.getReviewCountForRating(container, ratingValue);
    const showEmpty = visibleCount === 0 && expectedCount === 0;
    this.toggleEmptyFilterMessage(container, showEmpty);
    this.setupDesktopReviewLimit();

    return visibleCount;
  }

  resetReviewsForRenormalize(container) {
    this.getReviews(container).forEach((review) => {
      review.classList.remove('product-reviews-pilgrim__rev--filtered-out');
      review.classList.remove('product-reviews-pilgrim__rev--hidden-by-limit');
      delete review.dataset.pilgrimRevReady;
    });
  }

  activateStarFilter(container, rating) {
    this.activeStarFilter = String(rating);
    this.desktopExpanded = false;
    this.widgetNormalized = false;
    this.setActiveFilterButton(rating);
    this.toggleEmptyFilterMessage(container, false);

    const triggered = this.triggerStarFilter(container, rating);

    if (triggered) {
      this.resetReviewsForRenormalize(container);
      this.ensureObserver(container);
      this.scheduleNormalize();
      this.scheduleRetries();
      return;
    }

    this.applyStarFilter(container, rating);
    this.normalizeReviews(container);
  }

  deactivateStarFilter(container) {
    this.activeStarFilter = null;
    this.desktopExpanded = false;
    this.widgetNormalized = false;
    this.setActiveFilterButton(null);
    this.toggleEmptyFilterMessage(container, false);
    this.clearJudgemeFilter(container);
    this.resetReviewsForRenormalize(container);
    this.ensureObserver(container);
    this.scheduleNormalize();
    this.scheduleRetries();
  }

  clearStarFilter(container) {
    this.getReviews(container).forEach((review) => {
      review.classList.remove('product-reviews-pilgrim__rev--filtered-out');
      review.classList.remove('product-reviews-pilgrim__rev--hidden-by-limit');
    });

    this.activeStarFilter = null;
    this.toggleEmptyFilterMessage(container, false);
    this.setupDesktopReviewLimit();
  }

  triggerStarFilter(container, rating) {
    const trigger = this.getStarFilterTrigger(container, rating);
    if (!trigger) return false;

    trigger.click();
    return true;
  }

  clearJudgemeFilter(container) {
    const clearTrigger =
      container.querySelector('.jdgm-histogram__clear-filter') ||
      container.querySelector('.jdgm-star-filter .jdgm--active') ||
      container.querySelector('.jdgm-subtab__item.jdgm--active') ||
      container.querySelector('.jdgm-rev-widg__sort-wrapper .jdgm--active');

    if (!clearTrigger) return false;

    clearTrigger.click();
    return true;
  }

  setActiveFilterButton(rating) {
    const buttons = this.querySelectorAll('[data-filter-rating]');

    buttons.forEach((button) => {
      const isActive = rating && button.dataset.filterRating === String(rating);
      button.classList.toggle('product-reviews-pilgrim__star-filter-button--active', Boolean(isActive));
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  normalizeFilterEmpty(container) {
    const reviewsList = container.querySelector('.jdgm-rev-widg__reviews');
    if (!reviewsList) return false;

    let emptyMessage = container.querySelector('[data-pilgrim-filter-empty]');
    if (!emptyMessage) {
      const template = this.querySelector('[data-filter-empty-template]');
      if (!template?.content?.firstElementChild) return false;

      emptyMessage = template.content.firstElementChild.cloneNode(true);
      reviewsList.parentNode.insertBefore(emptyMessage, reviewsList);
    }

    return true;
  }

  normalizeStarFilter(container) {
    if (!container.querySelector('.jdgm-rev-widg')) return false;

    let filter = container.querySelector('[data-pilgrim-star-filter]');

    if (!filter) {
      const template = this.querySelector('[data-star-filter-template]');
      const reviewsList = container.querySelector('.jdgm-rev-widg__reviews');

      if (!template?.content?.firstElementChild || !reviewsList) return false;

      filter = template.content.firstElementChild.cloneNode(true);
      reviewsList.parentNode.insertBefore(filter, reviewsList);
    }

    this.normalizeFilterEmpty(container);
    container.classList.add('product-reviews-pilgrim__widget--has-pilgrim-star-filter');

    filter.querySelectorAll('[data-filter-rating]').forEach((button) => {
      if (button.getAttribute('aria-pressed') == null) {
        button.setAttribute('aria-pressed', 'false');
      }
    });

    return true;
  }

  onStarFilterClick(event) {
    const button = event.target.closest('[data-filter-rating]');
    if (!button || !this.contains(button)) return;

    event.preventDefault();

    const container = this.widgetContainer;
    if (!container) return;

    const rating = button.dataset.filterRating;
    const isActive = button.classList.contains('product-reviews-pilgrim__star-filter-button--active');

    if (isActive) {
      this.deactivateStarFilter(container);
      return;
    }

    this.activateStarFilter(container, rating);
  }

  normalizeWidget() {
    const container = this.widgetContainer;
    if (!container || this.isNormalizing) return;

    this.isNormalizing = true;
    this.disconnectObserver();

    try {
      container.classList.add('product-reviews-pilgrim__widget--ready');

      this.normalizeSummary(container);
      this.normalizeHistogram(container);
      this.normalizeStarFilter(container);
      this.normalizeReviews(container);
      this.normalizeLoadMore(container);
      this.normalizeTimestamps(container);
      this.setupDesktopReviewLimit();

      if (this.activeStarFilter) {
        const visibleCount = this.applyStarFilter(container, this.activeStarFilter);
        this.setActiveFilterButton(this.activeStarFilter);

        const expectedCount = this.getReviewCountForRating(container, this.activeStarFilter);
        if (visibleCount === 0 && expectedCount > 0) {
          this.toggleEmptyFilterMessage(container, false);
        }
      }

      if (this.isNormalizationComplete(container)) {
        this.widgetNormalized = true;
        this.disconnectObserver();
        this.clearRetryTimeouts();
        return;
      }

      this.ensureObserver(container);
    } finally {
      this.isNormalizing = false;
    }
  }

  ensureObserver(container) {
    if (this.widgetNormalized || this.observer) return;

    this.observer = new MutationObserver(() => {
      this.scheduleNormalize();
    });

    this.observer.observe(container, { childList: true, subtree: true });
  }

  scheduleRetries() {
    this.clearRetryTimeouts();

    [600, 1800].forEach((delay) => {
      const timeoutId = window.setTimeout(() => {
        if (!this.widgetNormalized) {
          this.normalizeWidget();
        }
      }, delay);

      this.retryTimeoutIds.push(timeoutId);
    });
  }

  setupDesktopReviewLimit() {
    const container = this.widgetContainer;
    const toggleButton = this.toggleButton;
    if (!container || !toggleButton) return;

    const allReviews = this.getReviews(container);
    const visibleReviews = allReviews.filter(
      (review) => !review.classList.contains('product-reviews-pilgrim__rev--filtered-out')
    );

    allReviews.forEach((review) => {
      review.classList.remove('product-reviews-pilgrim__rev--hidden-by-limit');
    });

    const showToggle = this.isDesktop && !this.desktopExpanded && visibleReviews.length > 2;

    if (!showToggle) {
      container.classList.remove('product-reviews-pilgrim__widget--desktop-collapsed');
      toggleButton.hidden = true;
      return;
    }

    visibleReviews.slice(2).forEach((review) => {
      review.classList.add('product-reviews-pilgrim__rev--hidden-by-limit');
    });

    container.classList.add('product-reviews-pilgrim__widget--desktop-collapsed');
    toggleButton.hidden = false;
  }

  onShowMoreClick() {
    const container = this.widgetContainer;
    const toggleButton = this.toggleButton;
    if (!container || !toggleButton) return;

    this.desktopExpanded = true;
    this.getReviews(container).forEach((review) => {
      review.classList.remove('product-reviews-pilgrim__rev--hidden-by-limit');
    });
    container.classList.remove('product-reviews-pilgrim__widget--desktop-collapsed');
    toggleButton.hidden = true;
    this.normalizeReviews(container);
  }

  onResize() {
    if (!this.isDesktop) {
      this.widgetContainer?.classList.remove('product-reviews-pilgrim__widget--desktop-collapsed');
      if (this.toggleButton) {
        this.toggleButton.hidden = true;
      }
      return;
    }

    this.setupDesktopReviewLimit();
  }

  async onReady() {
    const container = this.widgetContainer;
    if (!container) return;

    await this.waitForWidget(3000);
    if (!this.hasWidgetContent()) {
      const widgetHtml = await this.fetchProductReviewWidget();
      if (widgetHtml.trim()) {
        container.innerHTML = widgetHtml;
        this.triggerJudgemeInit();
        await this.waitForWidget(2000);
      }
    } else {
      this.triggerJudgemeInit();
    }

    this.normalizeWidget();
    this.scheduleRetries();
  }
}

customElements.define('product-reviews-judgeme', ProductReviewsJudgeme);
